import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React from 'react';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {getDefaultFabricShadowForType, getFabricShadowOrGlowTypeFromShadowType, isCustomShadow, isShadow} from '@Utils/fabric-shadow.util';
import {convertShadowOptionsBlurToFabricShadowBlur} from '@Utils/fabric-shadow.util';
import {getFabricShadowOffsetsFromShadowOptions} from '@Utils/fabric-shadow.util';
import {convertFabricShadowObjectToShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';
import type {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {BaseItemShadowOptions} from '@Components/poster-editor/components/base-item-shadow-options/base-item-shadow-options';

interface ItemShadowOptionProps {
  onClickColorFill(): void;
}

export function ItemShadowOption({onClickColorFill = (): void => {}}: ItemShadowOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;

  if (!activeItem) {
    throw new Error('activeItem does not exist in ItemShadowOption');
  }

  const shadow = useGetKeyFromActiveItem('shadow');
  if (shadow === undefined) {
    throw new Error('item does not have shadow property');
  }

  const shadowOptions = useMemo(() => {
    return convertFabricShadowObjectToShadowOrGlowOptionsObject(shadow, activeItem.getScaleForShadow());
  }, [shadow, activeItem]);

  const onToggleShadow = (newShadowType: ShadowType | null): void => {
    const newType = getFabricShadowOrGlowTypeFromShadowType(newShadowType);
    const currentType = shadow?.shadowOrGlowType;

    const updatedShadow = getDefaultFabricShadowForType(newType, activeItem.gitype, activeItem.getScaleForShadow());

    const shouldPreserveOffsets = isShadow(currentType) && isShadow(newType) && !isCustomShadow(currentType) && !isCustomShadow(newType);

    const shadowToApply = shouldPreserveOffsets ? {...updatedShadow, offsetX: shadow?.offsetX, offsetY: shadow?.offsetY} : updatedShadow;

    void activeItem.updateFromObject({shadow: shadowToApply});
  };

  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    if (!shadowOptions) {
      return;
    }

    const dropShadowColor = shadowOptions.color.slice() as RGBA;
    dropShadowColor[3] = dropShadowAlpha;
    void activeItem.updateFromObject(
      {
        shadow: {
          color: rgbaToHexString(dropShadowColor),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    if (!shadowOptions) {
      return;
    }

    void activeItem.updateFromObject(
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...shadowOptions, angle: dropShadowAngle}, activeItem.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateDistance = (dropShadowDistance: number, angle: number, undoable = true): void => {
    if (!shadowOptions) {
      return;
    }

    void activeItem.updateFromObject(
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...shadowOptions, distance: dropShadowDistance, angle}, activeItem.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateBlur = (dropShadowBlur: number, undoable = true): void => {
    if (!shadowOptions) {
      return;
    }

    void activeItem.updateFromObject(
      {
        shadow: {
          blur: convertShadowOptionsBlurToFabricShadowBlur(dropShadowBlur, activeItem.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseItemShadowOptions
      shadowOptions={shadowOptions}
      itemType={activeItem.gitype}
      onClickColorFill={(): void => {
        onClickColorFill();
      }}
      onToggleShadow={onToggleShadow}
      onUpdateOpacity={updateOpacity}
      onUpdateAngle={updateAngle}
      onUpdateDistance={updateDistance}
      onUpdateBlur={updateBlur}
    />
  );
}
